import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { collectDiagnostics } from '../../utils/errorDiagnostics';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/**
 * ErrorBoundary - Last line of defense.
 * MUST NOT use hooks, translations, or any external context.
 * All text is hardcoded to guarantee it never crashes itself.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorCode: null, reported: false, copied: false };
  }

  static getDerivedStateFromError(error) {
    const code = 'ERR-' + Math.random().toString(36).substring(2, 8).toUpperCase();
    return { hasError: true, error, errorCode: code };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error);
    this.reportError(error, errorInfo);
  }

  reportError = async (error, errorInfo) => {
    try {
      const diagnostics = collectDiagnostics(error, errorInfo, this.state.errorCode);
      const token = localStorage.getItem('smartcmr_token') || localStorage.getItem('token');
      await fetch(`${API_URL}/errors/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(diagnostics),
      });
      this.setState({ reported: true });
    } catch {
      // Silent
    }
  };

  handleRefresh = () => { window.location.reload(); };
  handleGoHome = () => { window.location.href = '/dashboard'; };
  handleCopyCode = () => {
    navigator.clipboard?.writeText(this.state.errorCode).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>

            <h1 className="text-lg font-bold text-slate-900 mb-1">Er ging iets mis</h1>
            <p className="text-sm text-slate-500 mb-4">Het probleem is automatisch gemeld aan ons team.</p>

            <div className="bg-slate-50 rounded-lg px-4 py-3 mb-5">
              <p className="text-xs text-slate-400 mb-1">Foutcode</p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-lg font-mono font-bold text-slate-800 tracking-wider">{this.state.errorCode}</p>
                <button onClick={this.handleCopyCode} className="p-1 rounded hover:bg-slate-200 transition-colors" title="Kopieer code">
                  {this.state.copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
              {this.state.reported && (
                <p className="text-xs text-emerald-600 mt-1">Volledige diagnose verstuurd naar SmartCMR</p>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRefresh} className="bg-blue-600 hover:bg-blue-700" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" /> Vernieuwen
              </Button>
              <Button variant="outline" onClick={this.handleGoHome} size="sm">
                <Home className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
